import { Strefa } from 'api/strefy/strefy.types';

/** Any zone tree node — `GET /strefa` and `GET /obecnosc/strefy` share this shape. */
type ZoneNode = { id: string; children: ZoneNode[] };

/** Pre-order walk — the same order the tree is rendered in. */
const flattenZones = <T extends ZoneNode>(zones: T[]): T[] =>
  zones.flatMap((zone) => [zone, ...flattenZones(zone.children as T[])]);

/** Ids of every zone that has children, i.e. every node the tree can expand. */
export const getExpandableZoneIds = (zones: ZoneNode[]): string[] =>
  flattenZones(zones)
    .filter((zone) => zone.children.length > 0)
    .map((zone) => zone.id);

export const getAllZoneCodes = (zones: Strefa[]): string[] => flattenZones(zones).map((zone) => zone.kod);

/** Ids of the ancestors of `zoneId`, root first; `null` when the zone is not in the tree. */
export const getZoneAncestorIds = (zones: Strefa[], zoneId: string, ancestorIds: string[] = []): string[] | null => {
  for (const zone of zones) {
    if (zone.id === zoneId) return ancestorIds;

    const found = getZoneAncestorIds(zone.children, zoneId, [...ancestorIds, zone.id]);
    if (found) return found;
  }

  return null;
};

const findZone = (zones: ZoneNode[], zoneId: string): ZoneNode | undefined =>
  flattenZones(zones).find((zone) => zone.id === zoneId);

/**
 * The row arrow expands / collapses everything below the clicked zone, not just one level
 * (Confluence 11. Strefy 1.4.2: "Button zwiń/rozwiń wszystko co poniżej").
 *
 * `nextExpanded` is what MUI TreeView proposes after the click: exactly one id is added or removed.
 */
export const toggleZoneSubtree = (zones: ZoneNode[], expanded: string[], nextExpanded: string[]): string[] => {
  const addedId = nextExpanded.find((id) => !expanded.includes(id));
  const removedId = expanded.find((id) => !nextExpanded.includes(id));
  const toggledId = addedId ?? removedId;
  const toggledZone = toggledId ? findZone(zones, toggledId) : undefined;

  if (!toggledZone) return nextExpanded;

  const subtreeIds = getExpandableZoneIds([toggledZone]);

  return addedId
    ? Array.from(new Set([...expanded, ...subtreeIds]))
    : expanded.filter((id) => !subtreeIds.includes(id));
};
